// Mirrors impire-workloads/wire/wire.go — the tenant verbs, the registration,
// the folded state a tenant sees, the placement records and the records
// verb. JSON field names are the Go file's; a shape that differs is a defect
// here. A subject is built by the library, never inlined.

import { subject, workloads } from "@impire-io/sdk";

import type { SolutionRef } from "./solutions.ts";

/** SERVICE is the workload service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "workloads";

/** The tenant verbs. */
export const Verb = {
  register: "register",
  list: "list",
  get: "get",
  update: "update",
  retire: "retire",
  suspend: "suspend",
  resume: "resume",
  records: "records",
  /** The ask for one of the platform's own workloads by name (decisions 0018, 0021): the auth service's at a tenant's creation, an admin's as the repair. */
  registerPlatform: workloads.VERB_REGISTER_PLATFORM,
} as const;

export type Verb = (typeof Verb)[keyof typeof Verb];

/** registerSubject is `workloads.register`. */
export function registerSubject(): string {
  return subject.client(subject.Family.workloads, Verb.register);
}

/** PlatformName is the name of one of the platform's own workloads: vault, state or observer. */
export const PlatformName = workloads.PlatformName;
export type PlatformName = workloads.PlatformName;

/**
 * registerPlatformSubject is `workloads.register-platform.<name>`
 * (01-the-workload-model.md § the verbs): the workload service fills the
 * image, the key and the staged secrets from the name; the console names
 * none of them.
 */
export function registerPlatformSubject(name: PlatformName): string {
  return workloads.registerPlatformSubject(name);
}

/**
 * PLATFORM_REGISTRANT is the registrant of a workload the platform registered
 * in the tenant itself — its vault and its state service, at its creation —
 * which a tenant sees and nobody in it updates or retires.
 */
export const PLATFORM_REGISTRANT = "platform";

/** Staged is one secret the executor stages beside the token for a platform workload: the platform-vault path, the file on the mount, the variable naming it. */
export interface Staged {
  path: string;
  file: string;
  env: string;
}

/** listSubject is `workloads.list`. */
export function listSubject(): string {
  return subject.client(subject.Family.workloads, Verb.list);
}

/** idSubject is `workloads.<verb>.<id>` for the verbs that name a workload. */
export function idSubject(verb: Verb, id: string): string {
  if (!validWorkloadID(id)) {
    throw new Error(`workloads: ${id} is not a workload id`);
  }
  return subject.client(subject.Family.workloads, verb, id);
}

/** The three kinds of placement record. */
export const RecordKind = workloads.RecordKind;
export type RecordKind = workloads.RecordKind;

/**
 * recordFilter is `placements.<tenant>.<workload>.<kind>.*`, the subject a
 * console pane subscribes for one workload's records of one kind from every
 * writer, admitted on the tenant's own token (04-placement-records.md § who
 * reads); the pane keeps the service's and the current placement's
 * executor's with heldBy (decision 0025).
 */
// The tenant account imports the stream as `placements.<slug>.>` unrenamed and
// the templates admit `placements.<slug>.>` (impire-auth/internal/accounts).
export function recordFilter(tenant: string, workload: string, kind: RecordKind): string {
  if (!subject.validSlug(tenant)) {
    throw new Error(`workloads: ${tenant} is not a slug`);
  }
  if (!validWorkloadID(workload)) {
    throw new Error(`workloads: ${workload} is not a workload id`);
  }
  return workloads.recordKindFilter(tenant, workload, kind);
}

/** heldBy reports whether a record's writer is the service or the executor holding the current placement. */
export const heldBy = workloads.heldBy;

/** PLACEMENT_HEADER names the placement a record is attributed to. */
export const PLACEMENT_HEADER = workloads.PLACEMENT_HEADER;

/** validWorkloadID reports whether s is a workload id: `w` and twelve lowercase hex characters. */
export function validWorkloadID(s: string): boolean {
  return /^w[0-9a-f]{12}$/.test(s);
}

/** validPlacementID reports whether s is a placement id: `p` and twelve lowercase hex characters. */
export function validPlacementID(s: string): boolean {
  return /^p[0-9a-f]{12}$/.test(s);
}

/** Status is what a tenant sees of a workload's placement: computed by the fold. */
export const Status = {
  pending: "pending",
  placing: "placing",
  running: "running",
  failing: "failing",
  unschedulable: "unschedulable",
  suspended: "suspended",
  retired: "retired",
} as const;

export type Status = (typeof Status)[keyof typeof Status];

/**
 * Reason says why a slot was released, or why an auction closed empty. The
 * second four are named on an unschedulable status and event (decision 0034):
 * the message beside them says it in words, naming what is missing.
 */
export const Reason = {
  failing: "failing",
  liveness: "liveness",
  drain: "drain",
  /** The registration's count no longer wants the slot (decision 0035). */
  scaled: "scaled",
  suspended: "suspended",
  retired: "retired",
  noExecutor: "no-executor",
  architecture: "architecture",
  requires: "requires",
  capacity: "capacity",
} as const;

export type Reason = (typeof Reason)[keyof typeof Reason];

/**
 * SlotState is one slot of a workload's folded entry (decision 0035): its
 * current placement, the executor holding it, its status, its failure count
 * and the generation it was placed at.
 */
export interface SlotState {
  /** From zero. */
  slot: number;
  placement?: string;
  executor?: string;
  status: Status;
  failures?: number;
  generation?: number;
  /** On unschedulable: why the auction closed empty (decision 0034). */
  reason?: Reason;
  /** On unschedulable: the reason in words. */
  message?: string;
}

/**
 * State is a workload's folded entry: a summary — the first slot's placement
 * and the executor holding it, the workload's status, the first slot's failure
 * count — and the slots beside it (decision 0035).
 */
export interface State {
  placement: string;
  executor: string;
  status: Status;
  /** The scoped key its placements are minted under; empty is `workload` (decision 0018). */
  key?: string;
  names?: string[];
  failures?: number;
  /** On unschedulable: why the auction closed empty (decision 0034). */
  reason?: Reason;
  /** On unschedulable: the reason in words, naming what is missing. */
  message?: string;
  seq: number;
  /** How many slots the record shows. */
  replicas?: number;
  /** How many of them run. */
  ready?: number;
  /** The workload's slots in order, one entry each. */
  slots?: SlotState[];
}

/** The registration states. */
export const RegistrationState = {
  active: "active",
  suspended: "suspended",
  retired: "retired",
} as const;

export type RegistrationState = (typeof RegistrationState)[keyof typeof RegistrationState];

/** Resources are a placement's virtual CPUs and memory. */
export interface Resources {
  cpus: number;
  memory_mib: number;
}

/** DEFAULT_MAX_REPLICAS bounds a registration's count when the install says nothing. */
export const DEFAULT_MAX_REPLICAS = 8;

/** Spec is what a registration's placements boot from, beside the count (decision 0035). */
export interface Spec {
  image: string;
  digest: string;
  architectures: string[];
  resources: Resources;
  requires?: string[];
  configuration?: Record<string, string>;
  egress?: string[];
  names?: string[];
}

/** LastGood is the spec of the latest generation one placement was confirmed running under. */
export interface LastGood extends Spec {
  generation: number;
}

/** Registration is the record the workload service holds for a workload. */
export interface Registration {
  id: string;
  tenant: string;
  name: string;
  description?: string;
  /** As the registrant named it. */
  image: string;
  /** The reference pinned to its digest. */
  digest: string;
  /** From the image's index: `linux/amd64`, `linux/arm64`. */
  architectures: string[];
  resources: Resources;
  /** The capabilities a host must advertise to carry a placement (decision 0034). */
  requires?: string[];
  configuration?: Record<string, string>;
  /** Rules the executor applies verbatim. */
  egress?: string[];
  /** What its placements answer discovery under — a model's names, a toolkit's name — unique in the tenant, vouched in each placement's attestation (decision 0026). */
  names?: string[];
  /** The slots the workload runs as; absent or zero is one (decision 0035). */
  replicas?: number;
  /** Raised at every change of the spec, never of the count; a slot behind it is rolled to it. */
  generation?: number;
  /** The spec of the latest generation one placement ran under; absent until one did. */
  last_good?: LastGood;
  /** On a platform registration only: `vault`, `state` or `observer`, the platform's own workload this is (decision 0018). */
  platform?: string;
  /** What the executor stages beside the token, on a platform registration. */
  staged?: Staged[];
  /** On a solution registration only: the solution, revision and role it is, and who installed it (02-DESIGN/14-solutions/02-authority.md § provenance). */
  solution?: SolutionRef;
  /** The principal's stable subject, or PLATFORM_REGISTRANT. */
  registrant: string;
  /**
   * A workload the managed workspace's steward registered from its
   * blueprint: nobody but the steward updates or retires it; an admin may
   * still stop and start it (02-DESIGN/13-blueprints/02-convergence.md § the two refusals).
   */
  required?: boolean;
  display?: string;
  state: RegistrationState;
  /** RFC 3339. */
  state_changed: string;
  state_by: string;
  /** RFC 3339. */
  created: string;
  /** RFC 3339. */
  updated: string;
}

/** RegisterRequest creates a registration. */
export interface RegisterRequest {
  name: string;
  description?: string;
  image: string;
  resources: Resources;
  requires?: string[];
  configuration?: Record<string, string>;
  egress?: string[];
  names?: string[];
  /** The slots to run as; absent is one. */
  replicas?: number;
}

/** RegisterReply is the workload id. */
export interface RegisterReply {
  id: string;
}

/**
 * UpdateRequest changes the image, resources, requirements, configuration,
 * egress, names or replicas; an absent field is unchanged. A change of the
 * spec is a new generation the slots are rolled to one at a time; a change of
 * the count alone opens or closes slots (decision 0035).
 */
export interface UpdateRequest {
  image?: string;
  resources?: Resources;
  requires?: string[];
  configuration?: Record<string, string>;
  egress?: string[];
  names?: string[];
  replicas?: number;
}

/** MAX_NAMES bounds the names one registration declares. */
export const MAX_NAMES = 16;

/** validName reports whether s is a name a registration may declare: `[A-Za-z0-9._-]+`, at most 64 characters, none of the platform's. */
export function validName(s: string): boolean {
  return /^[A-Za-z0-9._-]{1,64}$/.test(s) && !PLATFORM_NAMES.includes(s);
}

/** PLATFORM_NAMES are refused on every registration but the platform's own. */
export const PLATFORM_NAMES: readonly string[] = [
  "identity",
  "auth",
  "workloads",
  "config",
  "guard",
  "vault",
  "state",
  "observer",
];

/** Workload is one registration with its placement's status. */
export interface Workload extends Registration {
  placement: State;
}

/** ListReply is the tenant's registrations the caller may see. */
export interface ListReply {
  workloads: Workload[];
}

/** StateReply answers update, retire, suspend and resume. */
export interface StateReply {
  id: string;
  state: RegistrationState;
}

/** RecordsRequest asks for a page of one kind of a workload's records: newest first when from is zero, or forward from a sequence. */
export interface RecordsRequest {
  kind: RecordKind;
  limit?: number;
  from?: number;
}

/** MAX_RECORDS_PAGE bounds a page. */
export const MAX_RECORDS_PAGE = 500;

/** Record is one placement record as paged. */
export interface Record_ {
  seq: number;
  /** RFC 3339. */
  at: string;
  placement: string;
  body: unknown;
}

/** RecordsReply is a page; next is the sequence to continue from forward, or zero. */
export interface RecordsReply {
  records: Record_[];
  next?: number;
}

/** Output is one line the placement wrote. */
export interface Output {
  /** stdout | stderr */
  stream: string;
  line: string;
  /** RFC 3339. */
  at: string;
}

/** Sample is the runtime's metrics at an instant. */
export interface Sample {
  cpu_percent: number;
  cpus: number;
  memory_bytes: number;
  memory_host_resident_bytes: number;
  disk_read_bytes: number;
  disk_write_bytes: number;
  net_rx_bytes: number;
  net_tx_bytes: number;
  uptime_secs: number;
  /** RFC 3339. */
  at: string;
}

/** The events: the executor's and the fold's. */
export const EventKind = {
  booting: "booting",
  running: "running",
  died: "died",
  failing: "failing",
  assigned: "assigned",
  released: "released",
  suspended: "suspended",
  resumed: "resumed",
  retired: "retired",
  unschedulable: "unschedulable",
  /** A registration written before decision 0026 took its names from its settings, or could not. */
  namesDeclared: "names-declared",
  namesRefused: "names-refused",
  /** A tenant registration moved to the install's own image at start (decision 0026). */
  imageFollowed: "image-followed",
  /** A slot the registration's count closed (decision 0035). */
  scaled: "scaled",
  /** A generation that could not boot, the last good spec restored (decision 0035). */
  changeFailed: "change-failed",
} as const;

export type EventKind = (typeof EventKind)[keyof typeof EventKind];

/** Event is one lifecycle transition. */
export interface Event {
  event: EventKind;
  /** The slot, on every event of one; absent is slot zero. */
  slot?: number;
  attempt?: number;
  executor?: string;
  /** On released, and on unschedulable: why the auction closed empty. */
  reason?: string;
  /** On names-declared and names-refused. */
  names?: string[];
  /** Why, on names-refused, unschedulable and change-failed. */
  message?: string;
  /** On change-failed: the generation that could not boot. */
  generation?: number;
  /** On change-failed: the last good generation restored. */
  restored?: number;
  /** RFC 3339. */
  at: string;
}

/** The error codes the service answers with, beside the pattern's own. */
export const Code = {
  notFound: "not-found",
  conflict: "conflict",
  refused: "refused",
  /** A required workload's update or retire by anyone but the workspace's steward. */
  required: "required",
  /** The suspension of the workspace's steward. */
  managed: "managed",
} as const;

/** validEgressRule has the backend's grammar: `<action>[:<direction>]@<target>[:<proto>[:<ports>]]`. */
export function validEgressRule(rule: string): boolean {
  const at = rule.indexOf("@");
  if (at < 0 || at === rule.length - 1 || /[\s"']/.test(rule)) {
    return false;
  }
  const head = rule.slice(0, at);
  const [action, direction, ...more] = head.split(":");
  if (more.length > 0 || (action !== "allow" && action !== "deny")) {
    return false;
  }
  return direction === undefined || direction === "in" || direction === "out" || direction === "both";
}

/** validEnvName reports whether s is an environment variable name. */
export function validEnvName(s: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(s);
}
